import React, { useEffect, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Plus, X, Star, AlertTriangle, Copy, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { formatPhoneAsTyped } from '@/lib/phone';
import { uploadHeadshot } from '@/api/base44Client';
import AuthedImage from '@/components/AuthedImage';
import HeadshotCropModal from '@/components/HeadshotCropModal';

/** Where every rep's TradeSync-served card actually lives. Matches worker/lib/messages.ts's vcardLink() fallback exactly. */
const CARD_URL_BASE = 'https://api.mytradesync.com/m/';

/**
 * The rep profile, in one place, used by both create and edit on the Admin
 * screen.
 *
 * These are the values that used to live in a hardcoded REPS object inside two
 * backend functions, holding only adam and mike. That is why Aaron had an
 * active account and no calendar link, no vCard and no signature: he was not in
 * the object (CLAUDE.md section 5, bugs 3, 4 and 5). Every one of them is a
 * column and every one is editable here.
 *
 * Every URL here is free text with no pattern enforcement anywhere, in the
 * form or in the API, with one deliberate exception: the contact card URL
 * shown below the vCard URL Extension field is a read-only, derived display
 * (task: card link fields rework, 2026-09-15), not a field of its own. A
 * rep changing their extension and their card link silently staying on the
 * old value, because a separate vcard_url override was quietly still set
 * and winning, was a real footgun (the owner's own account hit it), so the
 * UI no longer offers a way to set that override at all. The vcard_url
 * column and worker/lib/messages.ts's vcardLink() precedence over
 * vcard_slug both still exist and still work exactly as before; only this
 * form's own involvement with vcard_url was removed, see EMPTY_PROFILE's
 * own comment. A rep's booking page happens to follow a pattern today, and
 * guessing would break on a second Adam or on anyone hosted elsewhere.
 * Paste the full address and the app uses exactly that.
 *
 * No placeholder may hold anybody's real details. A greyed out hint sitting in
 * an empty field on someone else's profile reads as if it were already theirs,
 * and it invites whoever is filling the form in to keep it. Phone examples use
 * the 555-01xx range reserved for fiction, names use Jane Smith, and the URL
 * fields show a bare https:// because any fuller example would imply a shape
 * these fields deliberately do not have.
 */

/** Migration 0013: up to 3 named calendar links per rep, one flagged default. */
export const MAX_CALENDAR_LINKS = 3;

// vcard_url deliberately absent (task: card link fields rework, 2026-09-15):
// this form no longer reads, displays or submits it at all, so saving a
// profile can never overwrite whatever is or is not already in that
// column. The Contact card URL shown to a rep is a derived display of
// vcard_slug instead, never vcard_url. If vcard_url is ever set directly
// (the column and worker/lib/messages.ts's vcardLink() precedence over
// vcard_slug both still exist), this form simply never touches it, on
// purpose.
export const EMPTY_PROFILE = {
  title: '',
  cell_phone: '',
  office_phone: '',
  calendar_links: [],
  scheduler_link: '',
  vcard_slug: '',
  headshot_url: '',
  headshot_key: '',
  linkedin_url: '',
  signature_html: '',
};

/**
 * Pull just the profile fields off a user, ready to edit. Phone fields are
 * formatted here too, so a number stored before as-you-type formatting
 * existed (or imported as a bare digit string) displays correctly the
 * moment this form opens, not only once someone retypes it.
 */
export function profileFrom(user) {
  const out = {};
  for (const key of Object.keys(EMPTY_PROFILE)) {
    if (key === 'calendar_links') continue;
    out[key] = user?.[key] ?? '';
  }
  // A real copy, not the same array reference, so editing it here never
  // mutates the query cache's own copy before Save is pressed.
  out.calendar_links = Array.isArray(user?.calendar_links) ? user.calendar_links.map(l => ({ ...l })) : [];
  out.cell_phone = formatPhoneAsTyped(out.cell_phone);
  out.office_phone = formatPhoneAsTyped(out.office_phone);
  return out;
}

// Split around the card link fields (rendered by CardLinkFields below, not
// this generic loop) so the two can sit together, extension first, in the
// order the task asked for, rather than wherever a single flat array would
// have put them.
const FIELDS_BEFORE_CARD_LINK = [
  { key: 'title', label: 'Job title', placeholder: 'e.g. Sales Director', autoCapitalize: 'none' },
  {
    key: 'cell_phone', label: 'Cell phone', placeholder: 'e.g. (555) 555-0123', required: true,
    phone: true, type: 'tel',
    // mobile vs work per the WHATWG autofill detail tokens: a contact hint
    // (home/work/mobile/fax/pager) precedes the field name, so plain "tel"
    // on both this and office_phone below would be indistinguishable to
    // iOS, the same bug found and fixed on the capture form and edit page.
    autoComplete: 'mobile tel',
  },
  {
    key: 'office_phone', label: 'Office phone', placeholder: 'e.g. (555) 555-0199',
    phone: true, type: 'tel', autoComplete: 'work tel',
  },
  {
    key: 'scheduler_link',
    label: 'Scheduler page (15/30/60)',
    placeholder: 'https://',
    help: 'Any URL. Shown only as a button on their home screen for booking on the fly at a show. Never sent in a message.',
  },
];

const FIELDS_AFTER_CARD_LINK = [
  { key: 'linkedin_url', label: 'LinkedIn URL', placeholder: 'https://' },
];

/**
 * vCard URL Extension (editable) and Contact card URL (read-only, derived),
 * in that order. Task: card link fields rework, 2026-09-15.
 *
 * Before this, Contact card URL was its own free-text override field, and
 * it silently won over the extension whenever both were set, exactly the
 * shape of footgun that let the owner's own card link stay on a stale
 * value after he changed his extension: nothing on screen showed the two
 * fields were in conflict, or which one actually won. The override itself
 * (vcard_url, and worker/lib/messages.ts's vcardLink() precedence rule)
 * still exists, still works, is just no longer reachable from this form:
 * see EMPTY_PROFILE's own comment for exactly where it can still be set
 * (directly against the database, or a PATCH /api/profile call naming it
 * explicitly, since worker/lib/userProfile.ts's PROFILE_FIELDS still
 * accepts it).
 */
function CardLinkFields({ value, onChange, disabled }) {
  const extension = (value.vcard_slug ?? '').trim();
  const derivedUrl = extension ? `${CARD_URL_BASE}${extension}` : '';

  const copy = () => {
    if (!derivedUrl) return;
    navigator.clipboard?.writeText(derivedUrl);
    toast.success('Copied');
  };

  return (
    <>
      <div>
        <Label className="text-xs text-muted-foreground">vCard URL Extension</Label>
        <Input
          value={value.vcard_slug ?? ''}
          onChange={(e) => onChange({ ...value, vcard_slug: e.target.value })}
          placeholder="e.g. janesmith"
          disabled={disabled}
          className="mt-1 h-9 md:text-sm"
        />
        <p className="text-[10px] text-muted-foreground/70 mt-0.5">
          The last part of your card's web address. Yours would be mytradesync.com/m/janesmith. Must be unique.
        </p>
      </div>

      <div>
        <Label className="text-xs text-muted-foreground">Contact card URL</Label>
        <div className="flex items-center gap-1.5 mt-1">
          <Input
            value={derivedUrl}
            placeholder="Set a vCard URL Extension above to get your link"
            readOnly
            className="h-9 md:text-sm bg-muted/50 text-muted-foreground cursor-default"
          />
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-9 w-9 shrink-0"
            onClick={copy}
            disabled={!derivedUrl}
            title="Copy"
          >
            <Copy className="w-3.5 h-3.5" />
          </Button>
        </div>
        <p className="text-[10px] text-muted-foreground/70 mt-0.5">
          This is what goes out in texts and emails. Set automatically from your vCard URL Extension above, not
          editable here.
        </p>
      </div>
    </>
  );
}

/**
 * File types this will actually try to upload. HEIC/HEIF is included even
 * though the Worker cannot decode it (worker/lib/images.ts): it is converted
 * to JPEG right here in the browser first (see handleFile below), so by the
 * time anything leaves this component it is always a format the server
 * understands. Some browsers report an empty or generic MIME type for a
 * HEIC file picked from a phone's photo library, which is why the extension
 * is checked too rather than trusting file.type alone.
 */
const HEADSHOT_ACCEPT = 'image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif';

function isHeicFile(file) {
  return /heic|heif/i.test(file.type) || /\.(heic|heif)$/i.test(file.name || '');
}

/**
 * Upload primary, "or paste a URL" secondary (migration 0018). headshot_key
 * wins on the read side whenever it is set (worker/routes/vcard.ts), so
 * uploading a new photo effectively replaces a pasted URL even though the
 * URL field itself is left untouched; Remove clears the key and falls back
 * to whatever URL, if any, is still in the field below.
 *
 * Picking a file no longer uploads it straight away (task: headshot crop
 * tool, 2026-09-15): it opens HeadshotCropModal on whatever was picked
 * (HEIC already converted to JPEG below, same as before), and only the
 * square the rep confirms there is what actually reaches uploadHeadshot.
 * Cancel returns to this field with nothing uploaded.
 *
 * Reordered and re-centered, task: 2026-09-25: rendered first, above every
 * other field, in this shared component (both the rep's own Profile page
 * and the admin's Edit Rep page show it this way), and its own internal
 * layout changed from a left-aligned photo-plus-buttons row to a centered
 * vertical stack, since it is now the first thing on the page rather than
 * one row among many.
 *
 * `large`, added the same day: Profile.jsx passes this (w-24 h-24, up from
 * w-14 h-14) since the request was specifically to enlarge the headshot on
 * the rep's own Profile page, not Edit Rep, which still gets the default
 * size. One prop rather than a second component, since everything else
 * about the field (upload, crop, URL fallback) is identical either way.
 */
function HeadshotField({ value, onChange, disabled, large = false }) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [cropFile, setCropFile] = useState(null);

  const handleFile = async (file) => {
    if (!file) return;
    setError('');

    try {
      let picked = file;

      if (isHeicFile(file)) {
        setUploading(true);
        // Dynamically imported so the ~2.7MB HEIC decoder only ever
        // downloads for the rare rep who actually picks a HEIC photo, not
        // on every visit to this page.
        const heic2any = (await import('heic2any')).default;
        const converted = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.92 });
        picked = Array.isArray(converted) ? converted[0] : converted;
      }

      setCropFile(picked);
    } catch (err) {
      setError(err?.message || 'Could not read that photo. Try a different file.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleCropCancel = () => setCropFile(null);

  const handleCropConfirm = async (croppedFile) => {
    setUploading(true);
    setError('');
    try {
      // false: this file is a freshly cropped canvas render (see
      // cropImage.js), never something with its own EXIF orientation tag
      // to honor, the same reasoning a HEIC-converted upload already used
      // here before this feature existed.
      const result = await uploadHeadshot(croppedFile, { honorExifOrientation: false });
      onChange({ ...value, headshot_key: result.key });
      setCropFile(null);
    } catch (err) {
      setError(err?.message || 'Could not upload that photo. Try a different file.');
    } finally {
      setUploading(false);
    }
  };

  const previewSrc = value.headshot_key ? `/api/images/${value.headshot_key}` : null;
  const sizeClass = large ? 'w-24 h-24' : 'w-14 h-14';

  return (
    <div className="flex flex-col items-center text-center">
      <Label className="text-xs text-muted-foreground">Headshot</Label>
      <div className="mt-2">
        {previewSrc ? (
          <AuthedImage
            src={previewSrc}
            alt="Headshot"
            className={`${sizeClass} rounded-full object-cover shrink-0`}
            fallbackClassName={`${sizeClass} rounded-full shrink-0`}
          />
        ) : value.headshot_url ? (
          <img src={value.headshot_url} alt="Headshot" className={`${sizeClass} rounded-full object-cover shrink-0`} />
        ) : (
          <div className={`${sizeClass} rounded-full bg-muted flex items-center justify-center text-muted-foreground text-[10px] shrink-0`}>
            None
          </div>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept={HEADSHOT_ACCEPT}
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
        disabled={disabled || uploading}
      />
      <div className="flex gap-2 mt-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="min-h-8 text-xs"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || uploading}
        >
          {uploading ? 'Working…' : 'Upload photo'}
        </Button>
        {value.headshot_key && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="min-h-8 text-xs text-muted-foreground"
            onClick={() => onChange({ ...value, headshot_key: '' })}
            disabled={disabled || uploading}
          >
            Remove
          </Button>
        )}
      </div>
      {error && <p className="text-[10px] text-destructive mt-1">{error}</p>}

      <Input
        value={value.headshot_url ?? ''}
        onChange={(e) => onChange({ ...value, headshot_url: e.target.value })}
        placeholder="https://"
        disabled={disabled}
        className="h-8 md:text-xs mt-3 w-full"
      />
      <p className="text-[10px] text-muted-foreground/70 mt-0.5">
        Upload a photo, or paste a URL to an already-hosted image. The uploaded photo is used
        whenever one exists, whether or not a URL is also set.
      </p>

      <HeadshotCropModal
        file={cropFile}
        open={!!cropFile}
        onCancel={handleCropCancel}
        onConfirm={handleCropConfirm}
      />
    </div>
  );
}

/**
 * The iframe's own internal rendering canvas: fixed, decoupled entirely
 * from the visible frame size below (task: signature preview still not
 * fitting, 2026-09-15, second pass). The first version gave the iframe
 * width: '100%', tying the framed document's own viewport, and therefore
 * its layout, to whatever width the visible pane happened to have; a
 * fixed-width email table does not reflow either way, but this still meant
 * the measurement and the render viewport were entangled for no reason.
 *
 * Deliberately close to a typical email body's own reading width (roughly
 * 600 to 650px, the real convention this project's own signature template
 * is built for), not something much larger: a wider canvas sounds safer,
 * "give it more room," but this template's outer wrapper is a
 * width:100% table, which genuinely stretches to fill however wide its
 * container is. An 800px canvas measured Marklen's actual, real signature
 * as 800px wide, not the roughly 600px it actually renders as, because
 * that outer table dutifully filled the extra room it was never going to
 * use. 700px leaves real headroom over both real signatures measured so
 * far (both close to 600x155) without inviting that gap back.
 */
const SIGNATURE_CANVAS_WIDTH = 700;
const SIGNATURE_CANVAS_HEIGHT = 450;
/** The visible pane's own size, deliberately roomier than before (task: a larger preview frame, 2026-09-15): the old pane was exactly as tall as the scaled content, which made a simple signature look like a cramped postage stamp. This is fixed regardless of what is being previewed; the content scales to fit inside it, never the other way around. */
const SIGNATURE_FRAME_HEIGHT = 288;

/**
 * Renders sanitized signature HTML at its own true, unmodified size on a
 * generous, fixed internal canvas (so a fixed-width table lays out exactly
 * as it would in a real email client), measures its actual rendered
 * bounding box, then visually scales and centers it with a CSS transform
 * to fit the visible pane with no scrolling in either direction. The
 * transform is purely cosmetic: it changes nothing about the HTML itself,
 * only how large and where the pane draws it, so what is measured and
 * shown here is byte for byte the same sanitized markup that would be
 * saved.
 *
 * Measured via getBoundingClientRect() over every LEAF element (one with
 * no element children of its own: an <img>, or a <span> holding only
 * text), unioned into one bounding box. Two things this is deliberately
 * not, both tried and rejected while chasing this same bug:
 *
 *   - body.scrollWidth/scrollHeight: does not reliably include content
 *     that only overflows its parent through float with nothing later
 *     clearing it, which is exactly what Aaron's and Marklen's four social
 *     icons are, floated <img> tags nested inside table cells.
 *   - Every element's bounding rect, leaves and containers alike: this
 *     project's own real signature template wraps its content in an outer
 *     <table style="width:100%">, which genuinely does stretch to fill
 *     whatever width the surrounding canvas happens to be, since nothing
 *     stops a percentage-width table from growing past what its own
 *     content needs. Measuring that container's own box (its first real
 *     attempt, also 2026-09-15) measured the canvas's width, not the
 *     signature's, and only ever looked right by accident when the canvas
 *     itself happened to be no wider than the real content.
 *
 * Leaf elements sidestep both: an image or a text run's own rendered
 * position reflects where the content actually is, never inflated by an
 * ancestor's own unrelated width, and floats are read the same as
 * anything else since getBoundingClientRect has no opinion on how an
 * element came to be positioned where it is.
 *
 * Re-measured on more than just the iframe's load event: a ResizeObserver
 * on the framed body catches a font or image that finishes sizing after
 * that first load fires, and a second one on the visible pane itself
 * catches the pane's own width changing later (a window resize, a device
 * rotation), neither of which a one-shot onLoad measurement would ever
 * see again.
 *
 * sandbox="allow-same-origin" (not the empty sandbox="" used before this
 * feature existed) is what lets this component's own script read the
 * iframe's rendered content and observe its size at all; a cross-origin
 * iframe cannot be measured from its parent. Scripts, forms, popups and
 * top navigation stay blocked either way (there is no allow-scripts,
 * allow-forms, allow-popups or allow-top-navigation token), so this does
 * not weaken the sandbox against active content, only against being
 * unreadable to the code that needs to size it. The real security
 * boundary remains server side, at save time
 * (worker/lib/sanitizeSignature.ts), unchanged by any of this.
 */
function SignaturePreview({ html }) {
  const frameRef = useRef(null);
  const iframeRef = useRef(null);
  const bodyObserverRef = useRef(null);
  const [transform, setTransform] = useState({ scale: 1, x: 0, y: 0 });

  const measure = () => {
    const frame = frameRef.current;
    const iframe = iframeRef.current;
    const body = iframe?.contentDocument?.body;
    if (!frame || !iframe || !body) return;

    let maxRight = 0;
    let maxBottom = 0;
    for (const el of body.querySelectorAll('*')) {
      if (el.children.length > 0) continue; // only leaves, see this component's own docblock for why
      const rect = el.getBoundingClientRect();
      if (rect.right > maxRight) maxRight = rect.right;
      if (rect.bottom > maxBottom) maxBottom = rect.bottom;
    }
    // Falls back to scrollWidth/scrollHeight only when there were no leaf
    // elements to measure at all (a bare text node signature, nothing but
    // text directly inside body): body.scrollWidth is exactly what this
    // component stopped trusting above, and must never be blended into the
    // real measurement as a floor, or it reintroduces the same inflated
    // width:100% problem this whole approach exists to avoid.
    const contentWidth = maxRight > 0 ? maxRight : Math.max(body.scrollWidth, 1);
    const contentHeight = maxBottom > 0 ? maxBottom : Math.max(body.scrollHeight, 1);

    const visibleWidth = frame.clientWidth;
    const visibleHeight = frame.clientHeight;

    // Never enlarges a signature that already fits (a plain text or
    // already-small one) just because the frame has room to spare.
    const scale = Math.min(1, visibleWidth / contentWidth, visibleHeight / contentHeight);
    // Centers the scaled content inside the visible frame in both
    // directions, rather than leaving it pinned to the top-left corner of
    // a frame now deliberately larger than most real content needs.
    const x = Math.max(0, (visibleWidth - contentWidth * scale) / 2);
    const y = Math.max(0, (visibleHeight - contentHeight * scale) / 2);
    setTransform({ scale, x, y });
  };

  const handleLoad = () => {
    measure();
    const body = iframeRef.current?.contentDocument?.body;
    if (!body) return;
    bodyObserverRef.current?.disconnect();
    bodyObserverRef.current = new ResizeObserver(measure);
    bodyObserverRef.current.observe(body);
  };

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return undefined;
    const frameObserver = new ResizeObserver(measure);
    frameObserver.observe(frame);
    return () => {
      frameObserver.disconnect();
      bodyObserverRef.current?.disconnect();
    };
  }, []);

  return (
    <div ref={frameRef} className="relative w-full overflow-hidden bg-white" style={{ height: SIGNATURE_FRAME_HEIGHT }}>
      <iframe
        ref={iframeRef}
        title="Signature preview"
        sandbox="allow-same-origin"
        srcDoc={`<style>html,body{margin:0;padding:0;}</style>${html}`}
        onLoad={handleLoad}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: SIGNATURE_CANVAS_WIDTH,
          height: SIGNATURE_CANVAS_HEIGHT,
          transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
          transformOrigin: '0 0',
          border: 0,
        }}
      />
    </div>
  );
}

/**
 * Paste-in textarea with a live preview, primary editing method: the
 * preview is DOMPurify run client side, on whatever is currently in the
 * textarea, purely so a rep can see a broken tag or a dead image before
 * saving, exactly the sandboxed-iframe pattern the admin's own email
 * preview tool (EventEmailEditor.jsx) already uses for the same reason.
 * This is defense in depth on an unsaved draft, not the real security
 * boundary: the actual sanitization that matters runs server side, at save
 * time (worker/lib/sanitizeSignature.ts), on the value that actually goes
 * out in a real email, so a stricter or laxer client preview can never be
 * the difference between safe and not.
 *
 * An "Upload HTML file" option sits next to the textarea (task: signature
 * file upload, 2026-09-15), a convenience only: it reads the file and
 * writes the same signature_html the textarea itself writes, so a rep can
 * review or edit it afterward and it goes through the exact same
 * sanitization on save. There is no separate upload endpoint and no
 * separate code path for a signature that arrived this way.
 */
/** Not tied to worker/lib/sanitizeSignature.ts's own limits, just a client side sanity cap so a huge paste cannot hang the preview; the real size limit that matters is whatever the API itself enforces on save. */
const MAX_SIGNATURE_FILE_BYTES = 500 * 1024;

function SignatureField({ value, onChange, disabled }) {
  const [previewHtml, setPreviewHtml] = useState('');
  const fileInputRef = useRef(null);
  const [fileError, setFileError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const raw = value.signature_html ?? '';

    if (!raw.trim()) {
      setPreviewHtml('');
      return undefined;
    }

    // Dynamically imported, same reasoning as heic2any above: this only
    // needs to load for someone actually looking at this field with a
    // signature already in it, not on every visit to this page.
    import('dompurify').then(({ default: DOMPurify }) => {
      if (!cancelled) setPreviewHtml(DOMPurify.sanitize(raw));
    });

    return () => {
      cancelled = true;
    };
  }, [value.signature_html]);

  // Reads the file into the exact same signature_html the textarea itself
  // writes (task: signature file upload, 2026-09-15). There is no
  // uploadSignature() call anywhere: this never leaves the browser or
  // touches an API, it only populates the textarea, so save still goes
  // through updateProfileFields' sanitizeSignatureHtml() the same as a
  // pasted signature always has, exactly once, in exactly one place.
  const handleFile = async (file) => {
    if (!file) return;
    setFileError('');

    if (file.size > MAX_SIGNATURE_FILE_BYTES) {
      setFileError(`That file is ${(file.size / 1024).toFixed(0)}KB. The limit is 500KB.`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    const looksLikeHtml = /\.(html?|htm)$/i.test(file.name || '') || /^text\/(html|plain)$/.test(file.type || '');
    if (!looksLikeHtml) {
      setFileError('That is not an HTML file. Upload a .html or .htm file.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    try {
      const text = await file.text();
      if (!text.trim()) {
        setFileError('That file is empty.');
        return;
      }
      onChange({ ...value, signature_html: text });
    } catch {
      setFileError('Could not read that file. Make sure it is a plain HTML or text file.');
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-2">
        <Label className="text-xs text-muted-foreground">Email signature (HTML)</Label>
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".html,.htm,text/html,text/plain"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
            disabled={disabled}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 text-[11px]"
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled}
          >
            <Upload className="w-3 h-3 mr-1" />
            Upload HTML file
          </Button>
        </div>
      </div>
      <Textarea
        value={value.signature_html ?? ''}
        onChange={(e) => onChange({ ...value, signature_html: e.target.value })}
        placeholder="Leave blank for a plain text signature"
        disabled={disabled}
        rows={6}
        className="mt-1 md:text-xs font-mono"
      />
      {fileError && <p className="text-[10px] text-destructive mt-1">{fileError}</p>}
      <p className="text-[10px] text-muted-foreground/70 mt-1">
        Sanitized again on save. Script, iframe, form and similar tags are removed, as are any
        on-click-style handlers; links must be https or mailto, images https or a data URL. Uploading a
        file just fills in the box above, so review or edit it here before saving.
      </p>

      {previewHtml && (
        <div className="mt-2 rounded-lg border border-border overflow-hidden">
          <div className="bg-secondary/40 px-2.5 py-1.5 border-b border-border">
            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Preview</p>
          </div>
          <SignaturePreview html={previewHtml} />
        </div>
      )}
    </div>
  );
}

/**
 * Which required profile fields are blank, so the asterisk, the "Required"
 * caption and the missing fields modal in Admin.jsx can never say three
 * different things about what is actually required. Same pattern as
 * REQUIRED_FIELDS in Shows.jsx. calendar_links has no entry here: having
 * none is a warning (below), not a hard requirement blocking Save.
 */
export function getMissingProfileFields(value) {
  return [...FIELDS_BEFORE_CARD_LINK, ...FIELDS_AFTER_CARD_LINK].filter(
    (f) => f.required && !(value[f.key] ?? '').trim(),
  );
}

/**
 * Up to 3 named links, one flagged default, both the capture form's
 * selector and the send itself (worker/lib/calendarLinks.ts) read from the
 * same list: there is no separate set for each purpose. Kept as its own
 * component since it is structured data, not a single string field like
 * everything else in FIELDS above.
 */
function CalendarLinksEditor({ links, onChange, disabled }) {
  const update = (index, patch) => {
    onChange(links.map((l, i) => (i === index ? { ...l, ...patch } : l)));
  };

  const makeDefault = (index) => {
    onChange(links.map((l, i) => ({ ...l, is_default: i === index })));
  };

  const remove = (index) => {
    const wasDefault = links[index].is_default;
    const next = links.filter((_, i) => i !== index);
    // Someone still has to be the default once one is removed, rather than
    // leaving the list in a state Save would have to silently repair.
    if (wasDefault && next.length > 0 && !next.some(l => l.is_default)) {
      next[0] = { ...next[0], is_default: true };
    }
    onChange(next);
  };

  const add = () => {
    if (links.length >= MAX_CALENDAR_LINKS) return;
    // No id yet: the backend mints a real one on save. Sending one back
    // unchanged is what keeps an *edited* link's id stable, since a contact
    // may already have chosen it by id.
    onChange([...links, { id: '', name: '', url: '', is_default: links.length === 0 }]);
  };

  return (
    <div>
      <Label className="text-xs text-muted-foreground">Calendar links</Label>
      <p className="text-[10px] text-muted-foreground/70 mt-0.5 mb-2">
        Up to {MAX_CALENDAR_LINKS}. The default is what goes out in every email and text, and what
        a capture preselects; a rep can switch to any other one per contact on the capture form.
      </p>

      {links.length === 0 && (
        <div className="flex items-start gap-1.5 text-[11px] text-amber-600 bg-amber-500/10 border border-amber-500/20 rounded-lg p-2 mb-2">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>No calendar links set. Every {'{calendarLink}'} line in this rep's templates, and the SMS, will be silently dropped.</span>
        </div>
      )}

      <div className="space-y-2">
        {links.map((link, i) => (
          <div key={i} className="rounded-lg border border-border p-2.5 space-y-1.5">
            <div className="flex items-center gap-1.5">
              <Input
                value={link.name}
                onChange={e => update(i, { name: e.target.value })}
                placeholder="e.g. 30 Minute Call"
                disabled={disabled}
                className="h-8 md:text-sm flex-1"
              />
              <button
                type="button"
                onClick={() => makeDefault(i)}
                disabled={disabled || link.is_default}
                title={link.is_default ? 'This is the default' : 'Make default'}
                className={`shrink-0 h-8 w-8 flex items-center justify-center rounded-md border ${
                  link.is_default
                    ? 'bg-accent/15 border-accent/40 text-accent'
                    : 'border-border text-muted-foreground hover:text-foreground'
                }`}
              >
                <Star className="w-3.5 h-3.5" fill={link.is_default ? 'currentColor' : 'none'} />
              </button>
              <button
                type="button"
                onClick={() => remove(i)}
                disabled={disabled}
                title="Remove"
                className="shrink-0 h-8 w-8 flex items-center justify-center rounded-md border border-border text-muted-foreground hover:text-destructive hover:border-destructive/40"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            <Input
              type="url"
              value={link.url}
              onChange={e => update(i, { url: e.target.value })}
              placeholder="https://"
              disabled={disabled}
              className="h-8 md:text-sm"
            />
            {link.is_default && <p className="text-[10px] text-accent font-medium">Default</p>}
          </div>
        ))}
      </div>

      {links.length < MAX_CALENDAR_LINKS && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={add}
          disabled={disabled}
          className="mt-2 min-h-8 text-xs w-full"
        >
          <Plus className="w-3.5 h-3.5 mr-1.5" />
          Add calendar link
        </Button>
      )}
    </div>
  );
}

function PlainField({ field, value, onChange, disabled }) {
  const { key, label, placeholder, help, required, phone, autoCapitalize, type, autoComplete } = field;
  return (
    <div>
      <Label className="text-xs text-muted-foreground">
        {label}
        {required && <span className="text-destructive"> *</span>}
      </Label>
      <Input
        type={type}
        value={value[key] ?? ''}
        onChange={(e) => onChange({ ...value, [key]: phone ? formatPhoneAsTyped(e.target.value) : e.target.value })}
        placeholder={placeholder}
        autoCapitalize={autoCapitalize}
        autoComplete={autoComplete}
        disabled={disabled}
        className="mt-1 h-9 md:text-sm"
      />
      {required && <p className="text-[10px] text-destructive mt-1">Required</p>}
      {help && <p className="text-[10px] text-muted-foreground/70 mt-0.5">{help}</p>}
    </div>
  );
}

export default function RepProfileFields({ value, onChange, disabled = false, largeHeadshot = false }) {
  const set = (key, next) => onChange({ ...value, [key]: next });

  return (
    <div className="space-y-3">
      {/* Headshot first, task: 2026-09-25. See HeadshotField's own comment
          for why it moved here and why its internal layout changed too. */}
      <HeadshotField value={value} onChange={onChange} disabled={disabled} large={largeHeadshot} />

      {FIELDS_BEFORE_CARD_LINK.map((field) => (
        <PlainField key={field.key} field={field} value={value} onChange={onChange} disabled={disabled} />
      ))}

      <CardLinkFields value={value} onChange={onChange} disabled={disabled} />

      {FIELDS_AFTER_CARD_LINK.map((field) => (
        <PlainField key={field.key} field={field} value={value} onChange={onChange} disabled={disabled} />
      ))}

      <CalendarLinksEditor
        links={value.calendar_links ?? []}
        onChange={(links) => set('calendar_links', links)}
        disabled={disabled}
      />

      <SignatureField value={value} onChange={onChange} disabled={disabled} />
    </div>
  );
}
